import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import InfinityList from '@/components/ui/custom/InfinityList';
import Skeleton from '@/components/ui/custom/Skeleton';
import {
  REQUEST_STALE_TIME_IN_MS as staleTime,
  MANAGEMENT_PAGE_SIZE as pageSize,
} from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import {
  enrollmentsQueryKeys,
  klassesProgressesQueryKeys,
  scheduledLessonsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import {
  activeEnrollmentStatus,
  inactiveEnrollmentStatus,
} from '@/functions/enrollment';
import Enrollment from '@/models/Enrollment';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import User from '@/models/User';
import KlassesInfoCol from './KlassesInfoCol';
import KlassesLessonCol from './KlassesLessonCol';

interface KlassProgressTableProps {
  klass?: Klass;
  loading?: boolean;
  error: any;
}

export default function TableContent(props: KlassProgressTableProps) {
  const { klass, loading, error: klassError } = props;

  const klassId = klass?.id;

  const enabled = !!klassId;

  const options = {
    enabled,
    staleTime,
  };

  const [isOnlyActive, setIsOnlyActive] = useState(true);

  const status = isOnlyActive
    ? activeEnrollmentStatus
    : inactiveEnrollmentStatus;

  const { data: klassProgress } = useQuery({
    ...options,
    ...klassesProgressesQueryKeys.get(klassId ?? 0),
  });

  const { data: teacher } = useQuery({
    ...usersQueryKeys.get(klass?.teacherId ?? 0),
    enabled: !!klass?.teacherId,
    staleTime,
  });

  const {
    results: enrollments,
    error: enrollmentError,
    isInitialLoading: isLoadingEnrollments,
  } = useListService({
    ...options,
    ...enrollmentsQueryKeys.list({
      klassId,
      status,
    }),
  });

  const {
    results: scheduledLessons,
    isInitialLoading: loadingLessons,
    error: lessonsError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      klassId: klass?.id,
      isActive: true,
      pageSize,
    })._ctx.infinity,
    ...options,
  });

  const error = klassError || enrollmentError || lessonsError;

  const { t: tPage } = useTranslation('translation', {
    keyPrefix: 'klassProgressPage',
  });

  if (loading) {
    return (
      <div className="flex gap-1.5">
        <Skeleton className="bg-primary-content rounded-2xl w-56 h-96" />
        <Skeleton className="bg-primary-content rounded-2xl w-full h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorComponent
        testId="errorComponent"
        statusCode={error?.response?.status}
        errorTextTitle={
          error?.response?.status === 404
            ? tPage('notFoundTitle')
            : tPage('genericErrorTitle')
        }
        errorTextSubTitle={
          error?.response?.status === 404
            ? tPage('notFoundText')
            : tPage('genericErrorText')
        }
      />
    );
  }

  if (klass) {
    return (
      <TableComponent
        teacher={teacher}
        enrollments={enrollments}
        klass={klass}
        klassProgress={klassProgress?.progress || 0}
        isFetchingNextPage={isFetchingNextPage}
        isLoadingEnrollments={isLoadingEnrollments}
        scheduledLessons={scheduledLessons}
        isOnlyActive={isOnlyActive}
        setIsOnlyActive={setIsOnlyActive}
        loadingLessons={loadingLessons}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    );
  }

  return null;
}

type TableComponentProps = {
  teacher?: User;
  enrollments: Enrollment[];
  klass: Klass;
  klassProgress: number;
  isLoadingEnrollments: boolean;
  loadingLessons: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  scheduledLessons: ScheduledLesson[];
  isOnlyActive: boolean;
  setIsOnlyActive: Dispatch<SetStateAction<boolean>>;
};

const TableComponent = ({
  teacher,
  enrollments,
  klass,
  klassProgress,
  isLoadingEnrollments,
  loadingLessons,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  scheduledLessons,
  isOnlyActive,
  setIsOnlyActive,
}: TableComponentProps) => {
  const { t } = useTranslation();

  return (
    <div className="w-full h-auto flex gap-1.5 relative">
      <div className="flex absolute top-16 left-2 text-14 items-center gap-2 w-fit">
        <input
          type="checkbox"
          defaultChecked={isOnlyActive}
          onChange={() => setIsOnlyActive(old => !old)}
          className="toggle toggle-sm toggle-primary mx-2"
        />
        <Text
          text={
            isOnlyActive
              ? t('klassProgressPage.active')
              : t('klassProgressPage.inactive')
          }
        />
      </div>
      <KlassesInfoCol
        enrollments={enrollments}
        klass={klass}
        klassProgress={klassProgress || 0}
        isLoading={isLoadingEnrollments}
        isOnlyActive={isOnlyActive}
      />
      <ConditionalRenderer
        condition={!loadingLessons}
        fallback={
          <Skeleton className="bg-primary-content rounded-2xl w-full h-auto" />
        }
      >
        <InfinityList
          className="flex flex-row h-full w-fit border-collapse py-3.5 scrollbar-thin 
          scrollbar-thumb-primary/40 scrollbar-track-primary-content scrollbar-thumb-rounded-full 
          scrollbar-track-rounded-full overflow-x-scroll rounded-2xl border border-neutral-content bg-base-100"
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onReachEnd={fetchNextPage}
        >
          {scheduledLessons.map(scheduledLesson => {
            return (
              <KlassesLessonCol
                teacher={teacher}
                key={scheduledLesson.id}
                klass={klass}
                enrollments={enrollments}
                scheduledLesson={scheduledLesson}
              />
            );
          })}
        </InfinityList>
      </ConditionalRenderer>
    </div>
  );
};
